import React from 'react';
import { MessageSquare, GitBranch, CheckSquare, ArrowDown, ArrowUp, AlertTriangle, Trash2 } from 'lucide-react';
import StageChip from './StageChip';

function fmt(at) {
  if (!at) return '';
  return String(at).slice(0, 16).replace('T', ' ');
}

// Keys we surface separately in the stage-transition header so they don't
// also show up in the structured-payload key/value list below.
const PAYLOAD_KEYS_RENDERED_ELSEWHERE = new Set([
  'summary',          // hoisted to the row's main text
  'force_stage',      // implied by the FORCED badge
  'override_reason',  // dedicated amber callout
  'classification',   // implementation detail (forward_skip / reverse / side / force_required)
  'skipped_stages',   // shown inline next to the from→to chips
]);

function humanKey(k) {
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanValue(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.map((x) => humanValue(x)).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v).replace(/_/g, ' ');
}

export default function TimelineEntry({ event, onDelete }) {
  if (!event) return null;
  if (event.kind === 'interaction') return <InteractionEntry it={event.interaction} at={event.at} onDelete={onDelete} />;
  if (event.kind === 'task_update') return <TaskEntry task={event.task} at={event.at} />;
  if (event.kind === 'stage_marker') return <StageMarker stage={event.stage} at={event.at} />;
  return null;
}

// System interactions are an immutable audit trail — the API rejects DELETE.
// Hide the trash icon when the row is system-typed so the affordance matches
// the server contract.
function canDelete(it, onDelete) {
  return !!(onDelete && it && it.type !== 'system');
}

function DeleteButton({ onClick, label = 'Delete interaction' }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid="timeline-delete"
      title={label}
      className="shrink-0 p-1 rounded text-gray-600 hover:text-red-300 hover:bg-red-500/10 transition-colors"
    >
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  );
}

function InteractionEntry({ it, at, onDelete }) {
  if (!it) return null;
  const isStageTransition = !!(it.stageFrom && it.stageTo);

  if (isStageTransition) return <StageTransitionEntry it={it} at={at} onDelete={onDelete} />;

  // Standard free-interaction row (Timeline tab "Log interaction" button).
  const Icon = it.direction === 'inbound' ? ArrowDown : ArrowUp;
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-gray-400 mt-0.5">
        <MessageSquare className="w-3 h-3" />
      </div>
      <div className="min-w-0 flex-1 rounded-xl border border-white/[0.05] bg-white/[0.015] p-3">
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <Icon className="w-3 h-3" />
          <span className="uppercase tracking-wider">{it.channel}</span>
          <span>·</span>
          <span>{fmt(at)}</span>
          {it.type === 'system' && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/[0.04] text-[9px] uppercase tracking-wider">system</span>}
          {canDelete(it, onDelete) && (
            <span className="ml-auto"><DeleteButton onClick={onDelete} /></span>
          )}
        </div>
        {it.subject && <div className="text-[13px] text-white font-medium mt-1">{it.subject}</div>}
        {it.summary && <div className="text-[12px] text-gray-300 leading-relaxed mt-1">{it.summary}</div>}
        {it.followUpNeeded && (
          <div className="text-[11px] text-amber-300 mt-2">Follow-up{it.followUpAt ? ` ${it.followUpAt}` : ''}: {it.followUpNotes || 'needed'}{it.relatedTaskId ? ' · task created' : ''}</div>
        )}
      </div>
    </div>
  );
}

function StageTransitionEntry({ it, at, onDelete }) {
  const payload = it.structuredPayload || {};
  const overrideReason = payload.override_reason || null;
  const skippedStages = Array.isArray(payload.skipped_stages) && payload.skipped_stages.length > 0
    ? payload.skipped_stages
    : null;

  // Filter the payload down to fields a human cares about. Hide internal
  // bookkeeping (force_stage, classification) and anything we render in the
  // dedicated header/callout above.
  const detailEntries = Object.entries(payload).filter(([k, v]) => {
    if (PAYLOAD_KEYS_RENDERED_ELSEWHERE.has(k)) return false;
    if (v == null || v === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });

  return (
    <div className="flex gap-3" data-testid="timeline-stage-transition">
      <div className="shrink-0 w-7 h-7 rounded-full bg-[#B0FC38]/10 border border-[#B0FC38]/30 flex items-center justify-center text-[#B0FC38] mt-0.5">
        <GitBranch className="w-3 h-3" />
      </div>
      <div className="min-w-0 flex-1 rounded-xl border border-white/[0.05] bg-white/[0.015] p-3 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Stage change</span>
          <StageChip stage={it.stageFrom} />
          <span className="text-gray-500 text-[12px]">→</span>
          <StageChip stage={it.stageTo} />
          {it.forcedSkip && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Forced
            </span>
          )}
          <span className="ml-auto text-[11px] text-gray-500">{fmt(at)}</span>
          {canDelete(it, onDelete) && (
            <DeleteButton onClick={onDelete} label="Delete stage-change entry" />
          )}
        </div>

        {skippedStages && (
          <div className="text-[11px] text-gray-500">
            <span className="text-gray-600">Skipped:</span> {skippedStages.map((s) => s.replace(/_/g, ' ')).join(', ')}
          </div>
        )}

        {it.summary && (
          <div className="text-[12.5px] text-gray-200 leading-relaxed">{it.summary}</div>
        )}

        {overrideReason && (
          <div className="px-3 py-2 rounded-lg bg-amber-500/[0.06] border border-amber-500/20 text-[12px] text-amber-200/90">
            <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mb-0.5">Override reason</div>
            {overrideReason}
          </div>
        )}

        {detailEntries.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-1">
            {detailEntries.map(([k, v]) => (
              <div key={k} className="text-[12px]">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">{humanKey(k)}</div>
                <div className="text-gray-200">{humanValue(v)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskEntry({ task, at }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-gray-400 mt-0.5">
        <CheckSquare className="w-3 h-3" />
      </div>
      <div className="min-w-0 flex-1 rounded-xl border border-white/[0.05] bg-white/[0.015] p-3">
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <span>Task {task?.status}</span>
          <span>·</span>
          <span>{fmt(at)}</span>
        </div>
        <div className="text-[12px] text-gray-300 mt-1">{task?.title}</div>
      </div>
    </div>
  );
}

function StageMarker({ stage, at }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-gray-400 mt-0.5">
        <GitBranch className="w-3 h-3" />
      </div>
      <div className="min-w-0 flex-1 rounded-xl border border-white/[0.05] bg-white/[0.015] p-3">
        <div className="flex items-center gap-2 text-[11px] text-gray-500">
          <span>Current stage</span>
          <span>·</span>
          <span>{fmt(at)}</span>
        </div>
        <div className="mt-1"><StageChip stage={stage} /></div>
      </div>
    </div>
  );
}
